import { RiCheckLine, RiCloseLine } from '@remixicon/react';
import { Flex, Text } from '@backstage/ui';
import { getComplianceColorVar } from '@internal/backstage-plugin-pulse-common';

const DEFAULT_SIZE = 100;
const STROKE_RATIO = 0.1;
const ICON_RATIO = 0.6;

// SVG circles start at 3 o'clock, so rotate the sweep back to top centre.
const ARC_START_DEGREES = -90;

type ComplianceGaugeProps = {
  percentage: number;
  label: string;
  size?: number;
  /** Fills the gauge with a solid disc once it reaches 100%. */
  fillOnComplete?: boolean;
};

export const ComplianceGauge = ({
  percentage,
  label,
  size = DEFAULT_SIZE,
  fillOnComplete = false,
}: ComplianceGaugeProps) => {
  const stroke = Math.round(size * STROKE_RATIO);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(percentage, 0), 100);
  const percentageLabel = clamped === 100 ? '100' : clamped.toFixed(1);
  const complianceColor = getComplianceColorVar(clamped);
  const isComplete = clamped === 100;
  const isEmpty = clamped === 0;
  const isFilled = isComplete && fillOnComplete;

  // Close the ring at 0% so the state reads as definitive rather than unstarted.
  const arcPercentage = isEmpty ? 100 : clamped;

  // Both extremes read faster as a glyph than as an arc that is full or absent.
  let StatusIcon: typeof RiCheckLine | undefined;
  if (isComplete) {
    StatusIcon = RiCheckLine;
  } else if (isEmpty) {
    StatusIcon = RiCloseLine;
  }

  return (
    <Flex align="center" gap="4">
      {/* The adjacent text already conveys the value to screen readers. */}
      <div
        aria-hidden
        style={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
        }}
      >
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--bui-bg-neutral-2)"
            strokeWidth={stroke}
          />
          {isFilled && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill={complianceColor}
            />
          )}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={complianceColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${
              (circumference * arcPercentage) / 100
            } ${circumference}`}
            transform={`rotate(${ARC_START_DEGREES} ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        </svg>
        {StatusIcon && (
          <StatusIcon
            size={Math.round(size * ICON_RATIO)}
            color={isFilled ? 'var(--bui-bg-surface-1)' : complianceColor}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        )}
      </div>

      <Flex direction="column" gap="1">
        <Text variant="title-small" weight="bold">
          {percentageLabel}%
        </Text>
        <Text variant="body-small" color="secondary">
          {label}
        </Text>
      </Flex>
    </Flex>
  );
};
